import Cookies from 'js-cookie';
import socket from '@/utils/socketInstance'; // Use the shared socket instance

/**
 * Simple EventEmitter implementation for browser
 * Compatible with Node.js EventEmitter API
 */
class EventEmitter {
  constructor() {
    this.events = {};
  }

  on(event, listener) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(listener);
  }

  off(event, listener) {
    if (!this.events[event]) return;
    if (listener) {
      this.events[event] = this.events[event].filter(l => l !== listener);
    } else {
      delete this.events[event];
    }
  }

  emit(event, ...args) {
    if (!this.events[event]) return;
    this.events[event].forEach(listener => {
      try {
        listener(...args);
      } catch (error) {
        console.error(`Error in event listener for ${event}:`, error);
      }
    });
  }

  once(event, listener) {
    const onceWrapper = (...args) => {
      listener(...args);
      this.off(event, onceWrapper);
    };
    this.on(event, onceWrapper);
  }

  removeAllListeners(event) {
    if (event) {
      delete this.events[event];
    } else {
      this.events = {};
    }
  }
}

/**
 * Socket Service
 * Wrapper around the shared socket instance from socketInstance.js
 * Uses the same socket connection to prevent multiple connections
 */

class SocketService extends EventEmitter {
  constructor() {
    super();
    this.socket = socket; // Use shared socket instance
    this.isConnected = false;
    this.hasSetupListeners = false;
  }

  /**
   * Connect to socket server
   * Socket is already connected via socketInstance.js, just setup listeners
   */
  connect() {
    if (this.hasSetupListeners) {
      return;
    }

    // Socket is already connected via socketInstance.js auto-connect
    // Just setup our custom event listeners
    this.setupEventListeners();
    this.hasSetupListeners = true;
  }

  /**
   * Setup socket event listeners
   * Uses the shared socket instance from socketInstance.js
   */
  setupEventListeners() {
    if (!this.socket) return;

    // Update connection status
    this.isConnected = this.socket.connected || false;

    // Listen to connection status changes
    this.socket.on('connect', () => {
      this.isConnected = true;
      this.emit('connect');
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      this.emit('disconnect', reason);
    });

    this.socket.on('connect_error', (error) => {
      this.emit('connect_error', error);
    });

    // Custom events from backend (maintaining compatibility)
    this.setupCustomEventListeners();
  }

  /**
   * Setup custom event listeners based on existing backend events
   * Maintains compatibility with existing socket implementation
   */
  setupCustomEventListeners() {
    if (!this.socket) return;

    // User info event (from C.USER_INFO)
    this.socket.on('user_info', (data) => {
      this.emit('user_info', this.decode(data));
    });

    // Game details event (from C.GAME_DETAILS)
    this.socket.on('game_details', (data) => {
      this.emit('game_details', this.decode(data));
    });

    // Site config updates (from siteconfig socket)
    this.socket.on('siteConfigUpdated', (data) => {
      console.log('📡 Socket: siteConfigUpdated received', data);
      this.emit('siteConfigUpdated', data);
    });

    this.socket.on('userConfigUpdated', (data) => {
      console.log('📡 Socket: userConfigUpdated received', data);
      this.emit('userConfigUpdated', data);
    });

    // Identify response
    this.socket.on('identify-error', (error) => {
      console.error('📡 Socket: identify error', error);
      this.emit('identify-error', error);
    });

    // Bonus timers
    this.socket.on('bonusTimerUpdate', (data) => {
      this.emit('bonusTimerUpdate', data);
    });

    // Credit/Wallet events
    this.socket.on('credit', (data) => {
      this.emit('credit', this.decode(data));
    });

    this.socket.on('update_credit', (data) => {
      this.emit('update_credit', this.decode(data));
    });

    // Powered by message (encoded)
    this.socket.on('U2t5cGU6IEBQZWRyb3hhbQ', (data) => {
      console.log('Powered by message received');
    });

    // Add more events as needed based on backend implementation
  }

  /**
   * Decode socket data (maintains compatibility with existing decode function)
   */
  decode(data) {
    try {
      if (data instanceof Blob) {
        const responseData = new TextDecoder().decode(data);
        return JSON.parse(responseData);
      }
      return data;
    } catch (e) {
      console.error('Error decoding socket data:', e);
      return data;
    }
  }

  /**
   * Encode data for socket (maintains compatibility)
   */
  encode(data) {
    try {
      return new Blob([JSON.stringify(data)]);
    } catch (e) {
      console.error('Error encoding socket data:', e);
      return data;
    }
  }

  /**
   * Emit event to server
   */
  emitToServer(event, data) {
    if (!this.socket || !this.isConnected) {
      console.warn('Socket not connected, cannot emit:', event);
      return;
    }
    this.socket.emit(event, data);
  }

  /**
   * Join room (for user-specific updates)
   */
  joinRoom(roomId) {
    if (!this.socket || !this.isConnected) {
      console.warn('Socket not connected, cannot join room');
      return;
    }
    this.socket.emit('join', roomId);
  }

  /**
   * Leave room
   */
  leaveRoom(roomId) {
    if (!this.socket || !this.isConnected) {
      return;
    }
    this.socket.emit('leave', roomId);
  }

  /**
   * Disconnect socket
   * Note: We don't disconnect the shared socket, just update our state
   */
  disconnect() {
    // Don't disconnect shared socket instance
    this.isConnected = false;
  }

  /**
   * Get socket instance (for advanced usage)
   */
  getSocket() {
    return this.socket;
  }

  /**
   * Check if socket is connected
   */
  getIsConnected() {
    return this.isConnected && this.socket?.connected;
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService;
